import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import { setPendingDiscard } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/pendingDiscardSignal';
import buildReviewPath from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/Helpers/buildReviewPath';
import type WorkflowRunDocumentView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

const tabStyle = (isSelected: boolean, isFailed: boolean) => css`
	padding: 0.625rem 1rem;
	border-radius: ${theme.radii.pill};
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	font-weight: ${isSelected ? theme.fontWeights.semibold : theme.fontWeights.medium};
	line-height: 1.2;
	background-color: ${
		isSelected ? theme.colors.ink : isFailed ? theme.states.danger.surface : theme.colors.surface
	};
	color: ${
		isSelected ? theme.colors.surface : isFailed ? theme.states.danger.solid : theme.colors.inkSecondary
	};
	border: 1.5px solid
		${isSelected ? theme.colors.ink : isFailed ? 'rgba(168, 30, 74, 0.25)' : theme.colors.borderStrong};
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
	}
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly stage: number;
	readonly document: WorkflowRunDocumentView;
	/** The document's type label, resolved by the parent from the account's catalogue. */
	readonly label: string;
	readonly isSelected: boolean;
};

/**
 * One document of the stage. A document that is not generated (failed, or still waiting) is not
 * openable: its tab says so and the retry lives in the preparation. Opening another document with a
 * modified draft queues a discard request rather than losing it.
 */
const ReviewStageTab: React.FC<Props> = ({ subject, preparationId, stage, document, label, isSelected }) => {
	const navigate = useNavigate();
	const documentId = document.documentId;
	const isFailed = document.isFailed;

	const handleClick = React.useCallback(() => {
		if (documentId === null || isSelected) {
			return;
		}

		const path = buildReviewPath(subject, documentId, preparationId, stage);

		if (editingFieldSignal.peek()?.isDirty) {
			setPendingDiscard({ type: 'back', path });
			return;
		}

		navigate(path, { replace: true });
	}, [navigate, subject, documentId, preparationId, stage, isSelected]);

	return (
		<button
			type="button"
			role="tab"
			aria-selected={isSelected}
			disabled={documentId === null}
			css={tabStyle(isSelected, isFailed)}
			onClick={handleClick}
		>
			{isFailed ? `${label} · échec` : label}
		</button>
	);
};

export default ReviewStageTab;
